
/**
 * Licenses Page - Data Table Implementation
 * Refactored for high data density and better information hierarchy (Stripe-like).
 */

import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Plus, RefreshCw } from 'lucide-react'
import { useLicenses, useRevokeLicense } from '@/hooks'
import { useUIStore } from '@/stores/ui-store'
import { toast } from 'sonner'
import { LicensesStats } from './LicensesStats'
import { LicensesFilters } from './LicensesFilters'
import { LicensesList } from './LicensesList'
import { CreateLicenseModal } from './CreateLicenseModal'
import type { FilterStatus, FilterPlan } from '../types'

// Sort configuration type
type SortField = 'licenseKey' | 'status' | 'plan' | 'usage' | 'createdAt'
type SortDirection = 'asc' | 'desc'

interface SortState {
  field: SortField
  direction: SortDirection
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export function LicensesPage() {
  const { data: licenses = [], isLoading, refetch } = useLicenses()
  const revokeMutation = useRevokeLicense()
  const { openModal } = useUIStore()

  // Local State
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const [filterPlan, setFilterPlan] = useState<FilterPlan>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sort, setSort] = useState<SortState>({ field: 'createdAt', direction: 'desc' })

  const licensesList = Array.isArray(licenses) ? licenses : (licenses?.data || [])

  // Helper function to get sort value for a license
  const getSortValue = (license: any, field: SortField) => {
    switch (field) {
      case 'licenseKey':
        return license.licenseKey.toLowerCase()
      case 'status':
        // Sort by status priority: revoked < expired < active
        const now = new Date()
        if (license.isRevoked) return 0
        if (new Date(license.expiresAt) < now) return 1
        return 2
      case 'plan':
        const planOrder = { starter: 0, professional: 1, enterprise: 2 }
        return planOrder[license.plan as keyof typeof planOrder] ?? 0
      case 'usage':
        return (license.tenants?.length || 0) / (license.maxTenants === -1 ? 999 : license.maxTenants)
      case 'createdAt':
        return new Date(license.createdAt).getTime()
      default:
        return ''
    }
  }

  // Filter Logic
  const filteredLicenses = useMemo(() => {
    return licensesList.filter((l: any) => {
      const matchesSearch = searchQuery === '' ||
        l.licenseKey.toLowerCase().includes(searchQuery.toLowerCase())

      let matchesStatus = true
      if (filterStatus === 'active') matchesStatus = !l.isRevoked && new Date(l.expiresAt) > new Date()
      else if (filterStatus === 'expired') matchesStatus = new Date(l.expiresAt) < new Date()
      else if (filterStatus === 'revoked') matchesStatus = l.isRevoked

      const matchesPlan = filterPlan === 'all' || l.plan === filterPlan

      return matchesSearch && matchesStatus && matchesPlan
    })
  }, [licensesList, searchQuery, filterStatus, filterPlan])

  // Sort Logic (applied after filtering)
  const sortedLicenses = useMemo(() => {
    const sorted = [...filteredLicenses]
    sorted.sort((a: any, b: any) => {
      const aValue = getSortValue(a, sort.field)
      const bValue = getSortValue(b, sort.field)

      if (sort.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })
    return sorted
  }, [filteredLicenses, sort])

  // Pagination Logic
  const totalPages = Math.ceil(sortedLicenses.length / itemsPerPage)
  const paginatedLicenses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return sortedLicenses.slice(startIndex, endIndex)
  }, [sortedLicenses, currentPage, itemsPerPage])

  // Reset to page 1 when filters or sort change
  useMemo(() => {
    setCurrentPage(1)
  }, [searchQuery, filterStatus, filterPlan, sort])

  // Handle sort change
  const handleSort = (field: SortField) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('License key copied')
  }

  const handleRevoke = (id: number) => {
    if (confirm('Are you sure? This will immediately stop all associated tenants.')) {
      revokeMutation.mutate(id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Licenses</h2>
          <p className="text-sm text-muted-foreground">Manage and monitor software license distribution.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="h-9">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => openModal('create-license')} className="h-9">
            <Plus className="h-4 w-4 mr-2" />
            Create License
          </Button>
        </div>
      </div>

      <LicensesStats licenses={licensesList} />

      <LicensesFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterPlan={filterPlan}
        setFilterPlan={setFilterPlan}
      />

      <LicensesList
        licenses={paginatedLicenses}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalCount={sortedLicenses.length}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        onRevoke={handleRevoke}
        onCopy={handleCopy}
        sort={sort}
        onSort={handleSort}
      />

      <CreateLicenseModal />
    </div>
  )
}
